import { useEffect, useState, useSyncExternalStore } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Modal,
  Select,
  Text,
} from "@shopify/polaris";
import {
  orderEventRequest,
  createOrderEventCommands,
} from "../../../../packages/shared/order-events/client";
import {
  queueAccountKey,
  type QueueAccount,
} from "../../../../packages/shared/shopify-queue/client";
type Event = {
  id: string;
  connection_id: string;
  domain: string;
  topic: string;
  order_id: number | null;
  order_gid: string | null;
  order_number: string | null;
  state: string;
  phase: string;
  revision: number;
  attempts: number;
  error: string | null;
  received_at: string;
  updated_at: string;
  next_attempt_at: string;
  reading_lines: number;
  reading_pages: number;
};
type Data = {
  connection: { id: string; domain: string; connected: number } | null;
  events: Event[];
  counts: { state: string; count: number }[];
  nextCursor: number | null;
};
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
const commands = new Map<string, ReturnType<typeof createOrderEventCommands>>();
const storage = {
  get length() {
    return window.localStorage.length;
  },
  key: (i: number) => window.localStorage.key(i),
  getItem: (k: string) => window.localStorage.getItem(k),
  setItem: (k: string, v: string) => window.localStorage.setItem(k, v),
  removeItem: (k: string) => window.localStorage.removeItem(k),
};
const labels: Record<string, string> = {
  pending: "Waiting",
  running: "Processing",
  failed: "Needs review",
  completed: "Completed",
  ignored: "Skipped",
  privacy_removed: "Personal details removed",
  import: "Read current Shopify order",
  allocate: "Reserve available stock",
  tracking: "Publish tracking and check initial email",
  done: "Finished",
};
export function OrderEventsPanel({
  account,
  canRetry,
}: {
  account: QueueAccount;
  canRetry: boolean;
}) {
  const key = queueAccountKey(account);
  if (!commands.has(key))
    commands.set(key, createOrderEventCommands(base, account, storage));
  const command = commands.get(key)!,
    state = useSyncExternalStore(command.subscribe, command.snapshot);
  const [data, setData] = useState<Data | null>(null),
    [error, setError] = useState(""),
    [filter, setFilter] = useState(""),
    [pages, setPages] = useState<number[]>([]),
    [refresh, setRefresh] = useState(0),
    [review, setReview] = useState<Event | null>(null);
  const cursor = pages[pages.length - 1],
    reload = () => {
      setPages([]);
      setRefresh((v) => v + 1);
    };
  useEffect(() => {
    const abort = new AbortController();
    setData(null);
    setError("");
    const query = new URLSearchParams();
    if (cursor) query.set("cursor", String(cursor));
    if (filter) query.set("state", filter);
    void orderEventRequest<Data>(base, account, "?" + query, {
      signal: abort.signal,
    })
      .then((value) => {
        if (!abort.signal.aborted) setData(value);
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [account, cursor, filter, refresh]);
  const locked =
    state.busy || !!state.pending || !canRetry || !data?.connection?.connected;
  const send = async () => {
    if (!review) return;
    const event = review;
    setReview(null);
    await command.submit({
      action: "retry",
      event_id: event.id,
      connection_id: event.connection_id,
      domain: event.domain,
      revision: event.revision,
    });
    if (command.snapshot().success) reload();
  };
  return (
    <BlockStack gap="400">
      <InlineStack gap="200">
        <Button onClick={reload}>Refresh order updates</Button>
        <Button url="/orders">View warehouse orders</Button>
      </InlineStack>
      <Text as="p">
        Shopify order updates are saved and processed in the background. Whenly
        reads the current order before applying changes. Conflicting picks,
        reservations or delivery work need review.
      </Text>
      <Text as="p">
        New eligible orders reserve available stock. A Shopify fulfillment
        message does not confirm a warehouse shipment or delivery. Initial
        customer emails are sent only when configured.
      </Text>
      {error && <Banner tone="critical">{error}</Banner>}
      {state.error && <Banner tone="critical">{state.error}</Banner>}
      {state.success && <Banner tone="success">{state.success}</Banner>}
      {state.pending && (
        <Banner tone="warning">
          <BlockStack gap="200">
            <Text as="p">
              A saved retry needs a confirmed reply. It remains saved after you
              close the browser.
            </Text>
            <Button
              disabled={!canRetry}
              loading={state.busy}
              onClick={() =>
                void command.retry().then(() => {
                  if (command.snapshot().success) reload();
                })
              }
            >
              Retry saved action
            </Button>
          </BlockStack>
        </Banner>
      )}
      <Select
        label="Show order updates"
        value={filter}
        options={[
          { label: "All updates", value: "" },
          ...[
            "pending",
            "running",
            "failed",
            "completed",
            "ignored",
            "privacy_removed",
          ].map((value) => ({ value, label: labels[value] })),
        ]}
        onChange={(value) => {
          setFilter(value);
          setPages([]);
        }}
      />
      {!data && !error && <Text as="p">Loading order updates…</Text>}
      {data && (
        <>
          {!data.connection?.connected && (
            <Banner tone="warning">
              Reconnect Shopify before retrying an order update.
            </Banner>
          )}
          <Text as="p">
            {data.counts.length
              ? data.counts
                  .map((c) => `${labels[c.state]}: ${c.count}`)
                  .join(" · ")
              : "No saved Shopify order updates yet."}
          </Text>
          {data.events.map((event) => (
            <Card key={event.id}>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">
                  {event.order_number || (event.order_gid ? "Shopify order " + event.order_gid.split("/").pop() : "Shopify order update")} ·{" "}
                  {labels[event.state]}
                </Text>
                <Text as="p">
                  {event.domain} · {event.topic} · Received {event.received_at}{" "}
                  UTC
                </Text>
                <Text as="p">
                  Stage: {labels[event.phase]}. Attempts: {event.attempts}. Last
                  change: {event.updated_at} UTC.
                </Text>
                {event.state === "pending" && (
                  <Text as="p">Next check: {event.next_attempt_at} UTC.</Text>
                )}
                {event.reading_lines > 0 && <Text as="p">{event.reading_lines} lines kept in {event.reading_pages} temporary pages. They have not been applied to the warehouse order. Reads resume from saved progress; expired pages are read again after seven days.</Text>}
                {event.error && <Banner tone="warning">{event.error}</Banner>}
                <InlineStack gap="200">
                  {event.order_id && (
                    <Button url={"/orders/" + event.order_id}>
                      View order
                    </Button>
                  )}
                  {event.state === "failed" && (
                    <Button
                      disabled={
                        locked ||
                        event.connection_id !== data.connection?.id ||
                        event.domain !== data.connection?.domain
                      }
                      onClick={() => setReview(event)}
                    >
                      Review retry
                    </Button>
                  )}
                </InlineStack>
              </BlockStack>
            </Card>
          ))}
          {!data.events.length && data.counts.length > 0 && (
            <Text as="p">No updates match this view.</Text>
          )}
        </>
      )}
      <InlineStack gap="200">
        {!!pages.length && (
          <Button onClick={() => setPages((p) => p.slice(0, -1))}>
            Newer updates
          </Button>
        )}
        {data?.nextCursor && (
          <Button onClick={() => setPages((p) => [...p, data.nextCursor!])}>
            Older updates
          </Button>
        )}
      </InlineStack>
      <Modal
        open={!!review}
        title="Review order update retry"
        onClose={() => setReview(null)}
        primaryAction={{
          content: "Confirm retry",
          disabled: locked,
          onAction: () => void send(),
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setReview(null) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p">
              Store: {review?.domain}. Order:{" "}
              {review?.order_number || review?.order_gid?.split("/").pop() || "Not imported yet"}.
            </Text>
            <Text as="p">
              Continue from: {review ? labels[review.phase] : ""}. Completed
              order and stock work stays saved. Conflicts must be resolved
              before the update can finish.
            </Text>
            <Text as="p">
              Tracking may be checked or published again. An email with an
              uncertain result is never sent again automatically. Check the
              email provider before retrying a tracking failure.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </BlockStack>
  );
}
